import { Injectable } from "@angular/core";
import { FetchStatusSelector } from "@obg/ngrx-utils";
import { isEqual } from "lodash-es";
import { Observable } from "rxjs";
import { distinctUntilChanged, map } from "rxjs/operators";

import { CallingCodeConfigService } from "../calling-code/calling-code.config";
import { CallingCode } from "../calling-code/calling-code.model";
import { CommonState } from "../state";

import { ACTION_PREFIX } from "./country.action";
import { AdministrativeArea, Country, CountryOrderType, CountryState, State, StateCity } from "./country.model";
import { CountryService } from "./country.service";

@Injectable({
	providedIn: "root",
})
/**
 * @deprecated [fabric-deprecated] - use mfe svc-content CountryService instead
 */
export class CountrySelector {
	constructor(
		private service: CountryService,
		private callingCodeConfig: CallingCodeConfigService
	) {}
	/**
	 * @deprecated [fabric-deprecated] - use "document.obgEventBus.retrieve(ContentValue.getCountryState)" instead
	 */
	getState() {
		return (state: CommonState): CountryState => state.country;
	}
	/**
	 * @deprecated [fabric-deprecated] - use "document.obgEventBus.retrieve(ContentValue.getCurrentCountry)" instead
	 */
	getCurrent() {
		return (state: CommonState): Country | undefined => state.country.currentSupportedCountry;
	}
	/**
	 * @deprecated [fabric-deprecated] - use "document.obgEventBus.retrieve(ContentValue.getCountryByCode)" instead
	 */
	getByCode(countryCode: string) {
		return (state: CommonState): Country | undefined =>
			state.country.countries && this.service.getByCode(state.country.countries, countryCode);
	}
	/**
	 * @deprecated [fabric-deprecated] - use "document.obgEventBus.retrieve(ContentValue.getCallingCodeByCountryCode)" instead
	 */
	getByCountryCode(countryCode: string) {
		return (state: CommonState): CallingCode | undefined =>
			state.callingCode.singleMarketCallingCodesWithCountryCode &&
			this.service.getByCountryCode(state.callingCode.singleMarketCallingCodesWithCountryCode, countryCode);
	}
	/**
	 * @deprecated [fabric-deprecated] - use "document.obgEventBus.retrieve(ContentValue.getCountryByCodes)" instead
	 */
	getByCodes(countryCodes: string[]) {
		return (state: CommonState): Country[] | undefined =>
			state.country.countries && this.service.getByCodes(state.country.countries, countryCodes);
	}
	/**
	 * @deprecated [fabric-deprecated] - not used
	 */
	isBusy() {
		return (state: CommonState): boolean => FetchStatusSelector.isBusy(ACTION_PREFIX)(state);
	}
	/**
	 * @deprecated [fabric-deprecated] - use "document.obgEventBus.retrieve(ContentValue.getCountryCallingCodes)" instead
	 */
	getCallingCodes() {
		return (state: CommonState): string[] | undefined =>
			state.country.countries &&
			(this.callingCodeConfig.getIsSingleMarketCallingCodesEnabled()
				? state.callingCode.singleMarketCallingCodes
				: this.service.getCallingCodes(state.country.countries, state.country.currentSupportedCountry));
	}
	/**
	 * @deprecated [fabric-deprecated] - use "document.obgEventBus.retrieve(ContentValue.getCountryCallingCodes)" instead
	 */
	getCallingCodes$() {
		return (state$: Observable<CommonState>): Observable<string[]> =>
			state$.pipe(
				distinctUntilChanged(undefined!, (state) => state.country),
				map(this.getCallingCodes()),
				distinctUntilChanged(isEqual)
			);
	}
	/**
	 * @deprecated [fabric-deprecated] - use "document.obgEventBus.retrieve(ContentValue.getCountriesByOrder)" instead
	 */
	getAll(order: CountryOrderType = CountryOrderType.Alphabetically) {
		return (state: CommonState): Country[] | undefined => {
			if (order === CountryOrderType.None) {
				return state.country.countries;
			}
			return (
				state.country.countries &&
				this.service.getOrderedCountries(state.country.countries, state.country.currentSupportedCountry, order)
			);
		};
	}
	/**
	 * @deprecated [fabric-deprecated] - use "document.obgEventBus.retrieve(ContentValue.getCountriesByOrder)" instead
	 */
	getAll$(order: CountryOrderType = CountryOrderType.Alphabetically) {
		return (state$: Observable<CommonState>): Observable<Country[] | undefined> =>
			state$.pipe(
				distinctUntilChanged(undefined!, (state) => state.country),
				map(this.getAll(order)),
				distinctUntilChanged(isEqual)
			);
	}
	/**
	 * @deprecated [fabric-deprecated] - use "document.obgEventBus.retrieve(ContentValue.getBirthCountriesByOrder)" instead
	 */
	getAllBirthCountries(order: CountryOrderType = CountryOrderType.Alphabetically) {
		return (state: CommonState): Country[] | undefined => {
			if (order === CountryOrderType.None) {
				return state.country.birthCountries;
			}
			return (
				state.country.birthCountries &&
				this.service.getOrderedCountries(
					state.country.birthCountries,
					state.country.currentSupportedCountry,
					order
				)
			);
		};
	}
	/**
	 * @deprecated [fabric-deprecated] - use "document.obgEventBus.retrieve(ContentValue.getCombinedCountriesByOrder)" instead
	 */
	getCombinedCountries(order: CountryOrderType = CountryOrderType.ByPriority) {
		return (state: CommonState): Country[] | undefined =>
			state.country.countries &&
			this.service.getCombinedCountries(
				state.country.countries,
				state.country.currentSupportedCountry,
				state.callingCode.singleMarketCallingCodesWithCountryCode,
				order
			);
	}
	/**
	 * @deprecated [fabric-deprecated] - use "document.obgEventBus.retrieve(ContentValue.getCombinedCountriesByOrder)" instead
	 */
	getCombinedCountries$(order: CountryOrderType = CountryOrderType.ByPriority) {
		return (state$: Observable<CommonState>): Observable<Country[]> =>
			state$.pipe(
				distinctUntilChanged(undefined!, (state) => state.country),
				map(this.getCombinedCountries(order)),
				distinctUntilChanged(isEqual)
			);
	}
	/**
	 * @deprecated [fabric-deprecated] - use "document.obgEventBus.retrieve(ContentValue.getCountryStates)" instead
	 */
	getCountryStates() {
		return (state: CommonState): State[] | undefined => state.country.states;
	}
	/**
	 * @deprecated [fabric-deprecated] - use "document.obgEventBus.retrieve(ContentValue.getStateCodeFromCityName)" instead
	 */
	getStateCodeFromCityName(cityName: string): (state: CommonState) => string | undefined {
		return (state) => this.service.getStateCodeFromCityName(state.country.states, cityName);
	}
	/**
	 * @deprecated [fabric-deprecated] - use "document.obgEventBus.retrieve(ContentValue.getCombinedCities)" instead
	 */
	getCombinedCities(blockedProvinces: string[] = []): (state: CommonState) => StateCity[] | undefined {
		return (state) => this.service.getCombinedCities(state.country.states, blockedProvinces);
	}
	/**
	 * @deprecated [fabric-deprecated] - no replacement because it is not used
	 */
	getCitiesByStateCode(code: string): (state: CommonState) => StateCity[] | undefined {
		return (state) => state.country.states?.find((nationState) => nationState.code === code)?.cities;
	}
	/**
	 * @deprecated [fabric-deprecated] - use "document.obgEventBus.retrieve(ContentValue.getAdministrativeAreasByCountryCode)" instead
	 */
	getAdministrativeAreasByCountryCode(countryCode: string): (state: CommonState) => AdministrativeArea | undefined {
		return (state) => state.country.administrativeAreasByCountryCode?.[countryCode];
	}
}
